import type { FormEvent } from "react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Person24Regular,
  Mail24Regular,
  Call24Regular,
  Shield24Regular,
  LockClosed24Regular,
  Eye20Regular,
  EyeOff20Regular,
  ArrowSync20Regular,
  Dismiss20Regular,
  Save20Regular,
} from "@fluentui/react-icons";
import { PageHeader } from "../../components/PageHeader";
import {
  CreateUserCommand,
  SwaggerException,
} from "../../web-api-client";
import { usersClient } from "../../users/usersClient";
import {
  calculatePasswordStrength,
  generatePassword,
} from "../../users/passwordUtils";
import { useAuth } from "../../auth/AuthContext";
import { accessControlClient } from "../../acessos/acessosClient";

type PerfilAcesso = "Administrador" | "Utilizador" | "Equipa interna";

const mapRole = (perfil: PerfilAcesso): string => {
  if (perfil === "Administrador") return "Administrator";
  if (perfil === "Equipa interna") return "TeamMember";
  return "User";
};

const parseErrorMessage = (error: unknown): string => {
  if (SwaggerException.isSwaggerException(error)) {
    if (error.status === 401) {
      return "Sessão expirada. Por favor, autentique-se novamente.";
    }
    if (error.status === 403) {
      return "Sem permissões para criar utilizadores.";
    }
    if (error.response) {
      try {
        const parsed = JSON.parse(error.response) as {
          errors?: Record<string, string[]>;
        };
        if (parsed?.errors) {
          const messages = Object.values(parsed.errors).flat();
          if (messages.length > 0) {
            return messages.join(" ");
          }
        }
      } catch {
        // Ignore invalid JSON.
      }
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return "Não foi possível criar o utilizador.";
};

export function NovoUtilizadorView() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [nome, setNome] = useState("");
  const [apelido, setApelido] = useState("");
  const [email, setEmail] = useState("");
  const [telefone, setTelefone] = useState("");
  const [perfil, setPerfil] = useState<PerfilAcesso>("Utilizador");
  // Cargos adicionais (os criados na Gestão de Acessos): um utilizador pode ter vários — o
  // acesso é a união do perfil base com estes.
  const [extraRoles, setExtraRoles] = useState<string[]>([]);
  const [customRoles, setCustomRoles] = useState<string[]>([]);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(
    null,
  );

  const isAdmin = user?.roles?.includes("Administrator") ?? false;

  useEffect(() => {
    let active = true;
    accessControlClient
      .getAccessControl()
      .then((vm) => {
        if (!active) return;
        setCustomRoles(
          (vm.roles ?? [])
            .filter((role) => !role.isSystem)
            .map((role) => role.name ?? "")
            .filter((name) => name !== ""),
        );
      })
      .catch(() => {
        // Sem a lista, o formulário funciona só com o perfil base.
      });
    return () => {
      active = false;
    };
  }, []);

  const passwordStrength = useMemo(
    () => (password ? calculatePasswordStrength(password) : null),
    [password],
  );

  const passwordsMatch =
    password.length > 0 &&
    confirmPassword.length > 0 &&
    password === confirmPassword;

  const canSubmit =
    isAdmin &&
    !isSaving &&
    nome.trim().length > 0 &&
    apelido.trim().length > 0 &&
    email.trim().length > 0 &&
    password.length > 0 &&
    confirmPassword.length > 0 &&
    passwordsMatch;

  const handleGeneratePassword = () => {
    const generated = generatePassword();
    setPassword(generated);
    setConfirmPassword(generated);
    setShowPassword(true);
    setShowConfirmPassword(true);
    setSubmitError(null);
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitError(null);

    if (!passwordsMatch) {
      setSubmitError("As passwords não coincidem.");
      return;
    }

    setIsSaving(true);

    try {
      await usersClient.createUser(
        new CreateUserCommand({
          email: email.trim(),
          password,
          firstName: nome.trim(),
          lastName: apelido.trim(),
          phoneNumber: telefone.trim() || undefined,
          role: mapRole(perfil),
          roles: [mapRole(perfil), ...extraRoles],
        }),
      );

      navigate("/utilizadores", {
        state: { toast: "Utilizador criado com sucesso" },
      });
    } catch (error) {
      setSubmitError(parseErrorMessage(error));
    } finally {
      setIsSaving(false);
    }
  };

  const actions = (
    <div className="flex items-center gap-4">
      <button
        className="px-4 py-2 bg-secondary hover:bg-accent border border-border rounded transition-colors flex items-center gap-2 min-h-[44px]"
        style={{ borderRadius: "var(--radius-button)" }}
        onClick={() => navigate("/utilizadores")}
        disabled={isSaving}
      >
        <Dismiss20Regular className="w-5 h-5" />
        <span className="hidden sm:inline">Cancelar</span>
      </button>
      <button
        className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded transition-colors flex items-center gap-2 min-h-[44px] disabled:opacity-50 disabled:cursor-not-allowed"
        style={{
          borderRadius: "var(--radius-button)",
          height: "var(--button-height)",
        }}
        onClick={() => {
          const form = document.getElementById(
            "novo-utilizador-form",
          ) as HTMLFormElement | null;
          form?.requestSubmit();
        }}
        disabled={!canSubmit}
      >
        <Save20Regular className="w-5 h-5" />
        {isSaving ? "A criar..." : "Criar Utilizador"}
      </button>
    </div>
  );

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Novo Utilizador"
        subtitle="Adicione um novo utilizador à equipa e defina as permissões de acesso."
        actions={actions}
        backButton={{ onClick: () => navigate("/utilizadores") }}
      />

      <div className="flex-1 overflow-y-auto bg-background">
        <div className="p-4 md:p-6">
          {!isAdmin && (
            <div
              className="bg-card border border-border px-4 py-3 mb-6 text-secondary-text"
              style={{ borderRadius: "var(--radius-card)" }}
            >
              Sem permissões para criar utilizadores.
            </div>
          )}

          {submitError && (
            <div
              className="bg-card border border-[var(--destructive)] px-4 py-3 mb-6 text-[var(--destructive)]"
              style={{ borderRadius: "var(--radius-card)" }}
            >
              {submitError}
            </div>
          )}

          <form
            id="novo-utilizador-form"
            onSubmit={handleSubmit}
            autoComplete="off"
          >
            <div
              className="bg-card p-6 mb-6"
              style={{ borderRadius: "var(--radius-card)" }}
            >
              <h2 className="text-card-foreground mb-6 flex items-center gap-2">
                <Person24Regular />
                Informação Básica
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label
                    htmlFor="nome"
                    className="block text-card-foreground mb-2"
                  >
                    Nome{" "}
                    <span className="text-[var(--destructive)]">
                      *
                    </span>
                  </label>
                  <input
                    id="nome"
                    type="text"
                    value={nome}
                    onChange={(event) => setNome(event.target.value)}
                    required
                    placeholder="Ex: Maria"
                    className="w-full h-10 px-3 bg-[var(--input-background)] border border-[var(--border)] text-card-foreground placeholder:text-[var(--placeholder-text)] outline-none transition-all focus:border-[var(--ring)] focus:ring-2 focus:ring-[var(--ring)] focus:ring-opacity-20"
                    style={{ borderRadius: "var(--radius)" }}
                    disabled={!isAdmin}
                  />
                </div>

                <div>
                  <label
                    htmlFor="apelido"
                    className="block text-card-foreground mb-2"
                  >
                    Apelido{" "}
                    <span className="text-[var(--destructive)]">
                      *
                    </span>
                  </label>
                  <input
                    id="apelido"
                    type="text"
                    value={apelido}
                    onChange={(event) =>
                      setApelido(event.target.value)
                    }
                    required
                    placeholder="Ex: Silva"
                    className="w-full h-10 px-3 bg-[var(--input-background)] border border-[var(--border)] text-card-foreground placeholder:text-[var(--placeholder-text)] outline-none transition-all focus:border-[var(--ring)] focus:ring-2 focus:ring-[var(--ring)] focus:ring-opacity-20"
                    style={{ borderRadius: "var(--radius)" }}
                    disabled={!isAdmin}
                  />
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="block text-card-foreground mb-2"
                  >
                    Email{" "}
                    <span className="text-[var(--destructive)]">
                      *
                    </span>
                  </label>
                  <div className="relative">
                    <Mail24Regular className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted-foreground)]" />
                    <input
                      id="email"
                      name="new-email"
                      type="email"
                      value={email}
                      onChange={(event) =>
                        setEmail(event.target.value)
                      }
                      required
                      placeholder="maria.silva@aja2.pt"
                      autoComplete="off"
                      className="w-full h-10 pl-10 pr-3 bg-[var(--input-background)] border border-[var(--border)] text-card-foreground placeholder:text-[var(--placeholder-text)] outline-none transition-all focus:border-[var(--ring)] focus:ring-2 focus:ring-[var(--ring)] focus:ring-opacity-20"
                      style={{ borderRadius: "var(--radius)" }}
                      disabled={!isAdmin}
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="telefone"
                    className="block text-card-foreground mb-2"
                  >
                    Telefone
                  </label>
                  <div className="relative">
                    <Call24Regular className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted-foreground)]" />
                    <input
                      id="telefone"
                      type="tel"
                      value={telefone}
                      onChange={(event) =>
                        setTelefone(event.target.value)
                      }
                      placeholder="+351 210 000 000"
                      className="w-full h-10 pl-10 pr-3 bg-[var(--input-background)] border border-[var(--border)] text-card-foreground placeholder:text-[var(--placeholder-text)] outline-none transition-all focus:border-[var(--ring)] focus:ring-2 focus:ring-[var(--ring)] focus:ring-opacity-20"
                      style={{ borderRadius: "var(--radius)" }}
                      disabled={!isAdmin}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div
              className="bg-card p-6 mb-6"
              style={{ borderRadius: "var(--radius-card)" }}
            >
              <h2 className="text-card-foreground mb-6 flex items-center gap-2">
                <Shield24Regular />
                Permissões e Acesso
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label
                    htmlFor="perfil"
                    className="block text-card-foreground mb-2"
                  >
                    Perfil de acesso{" "}
                    <span className="text-[var(--destructive)]">
                      *
                    </span>
                  </label>
                  <select
                    id="perfil"
                    value={perfil}
                    onChange={(event) =>
                      setPerfil(event.target.value as PerfilAcesso)
                    }
                    required
                    className="w-full h-10 px-3 bg-[var(--input-background)] border border-[var(--border)] text-card-foreground outline-none transition-all focus:border-[var(--ring)] focus:ring-2 focus:ring-[var(--ring)] focus:ring-opacity-20"
                    style={{ borderRadius: "var(--radius)" }}
                    disabled={!isAdmin}
                  >
                    <option value="Utilizador">Utilizador</option>
                    <option value="Administrador">Administrador</option>
                    <option value="Equipa interna">Equipa interna</option>
                  </select>

                  <p className="text-xs text-[var(--muted-foreground)] mt-1">
                    {perfil === "Administrador" &&
                      "Vê e gere tudo. Nenhum cargo ou funcionalidade o limita."}
                    {perfil === "Utilizador" &&
                      "Acede às funcionalidades do cargo «Utilizador» (definidas na Gestão de Acessos)."}
                    {perfil === "Equipa interna" &&
                      "Só a Folha de Horas e o próprio perfil (trabalhadores e encarregados de obra)."}
                  </p>

                  {customRoles.length > 0 && (
                    <div
                      className="mt-3 border border-[var(--border)] p-3"
                      style={{ borderRadius: "var(--radius)" }}
                    >
                      <span id="extra-roles-label" className="block text-card-foreground text-sm mb-1" style={{ fontWeight: "var(--font-weight-medium)" }}>
                        Cargos adicionais
                      </span>
                      <p className="text-xs text-[var(--muted-foreground)] mb-2">
                        Somam-se ao perfil acima. Geridos na Gestão de Acessos.
                      </p>
                      <div role="group" aria-labelledby="extra-roles-label" className="flex flex-col gap-1">
                        {customRoles.map((roleName) => (
                          <label key={roleName} className="flex items-center gap-2 text-sm text-card-foreground py-1" style={{ cursor: "pointer" }}>
                            <input
                              type="checkbox"
                              checked={extraRoles.includes(roleName)}
                              disabled={!isAdmin}
                              onChange={() =>
                                setExtraRoles((previous) =>
                                  previous.includes(roleName)
                                    ? previous.filter((name) => name !== roleName)
                                    : [...previous, roleName],
                                )
                              }
                            />
                            {roleName}
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div
              className="bg-card p-6 mb-6"
              style={{ borderRadius: "var(--radius-card)" }}
            >
              <h2 className="text-card-foreground mb-6 flex items-center gap-2">
                <LockClosed24Regular />
                Password
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label
                    htmlFor="password"
                    className="block text-card-foreground mb-2"
                  >
                    Password{" "}
                    <span className="text-[var(--destructive)]">
                      *
                    </span>
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      name="new-password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(event) =>
                        setPassword(event.target.value)
                      }
                      required
                      placeholder="Password"
                      autoComplete="new-password"
                      className="w-full h-10 px-3 bg-[var(--input-background)] border border-[var(--border)] text-card-foreground placeholder:text-[var(--placeholder-text)] outline-none transition-all focus:border-[var(--ring)] focus:ring-2 focus:ring-[var(--ring)] focus:ring-opacity-20"
                      style={{ borderRadius: "var(--radius)" }}
                      disabled={!isAdmin}
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--muted-foreground)]"
                      onClick={() =>
                        setShowPassword((prev) => !prev)
                      }
                      disabled={!isAdmin}
                    >
                      {showPassword ? <EyeOff20Regular /> : <Eye20Regular />}
                    </button>
                  </div>
                  {passwordStrength && (
                    <div
                      className="mt-1"
                      style={{ color: passwordStrength.color }}
                    >
                      <strong>Força da Password:</strong>{" "}
                      {passwordStrength.label}
                    </div>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="confirmPassword"
                    className="block text-card-foreground mb-2"
                  >
                    Confirmar Password{" "}
                    <span className="text-[var(--destructive)]">
                      *
                    </span>
                  </label>
                  <div className="relative">
                    <input
                      id="confirmPassword"
                      name="confirm-new-password"
                      type={showConfirmPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(event) =>
                        setConfirmPassword(event.target.value)
                      }
                      required
                      placeholder="Confirmar Password"
                      autoComplete="new-password"
                      className="w-full h-10 px-3 bg-[var(--input-background)] border border-[var(--border)] text-card-foreground placeholder:text-[var(--placeholder-text)] outline-none transition-all focus:border-[var(--ring)] focus:ring-2 focus:ring-[var(--ring)] focus:ring-opacity-20"
                      style={{ borderRadius: "var(--radius)" }}
                      disabled={!isAdmin}
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--muted-foreground)]"
                      onClick={() =>
                        setShowConfirmPassword((prev) => !prev)
                      }
                      disabled={!isAdmin}
                    >
                      {showConfirmPassword ? (
                        <EyeOff20Regular />
                      ) : (
                        <Eye20Regular />
                      )}
                    </button>
                  </div>
                  {!passwordsMatch &&
                    password.length > 0 &&
                    confirmPassword.length > 0 && (
                      <div className="mt-1 text-[var(--destructive)]">
                        As passwords não coincidem!
                      </div>
                    )}
                </div>
              </div>

              <div className="mt-4">
                <button
                  type="button"
                  className="px-4 py-2 bg-accent hover:bg-accent/90 text-accent-foreground rounded transition-colors flex items-center gap-2 min-h-[44px] disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ borderRadius: "var(--radius-button)" }}
                  onClick={handleGeneratePassword}
                  disabled={!isAdmin}
                >
                  <ArrowSync20Regular className="w-5 h-5" />
                  Gerar Password Segura
                </button>
              </div>
            </div>

            <div className="flex gap-3 md:hidden">
              <button
                type="button"
                onClick={() => navigate("/utilizadores")}
                disabled={isSaving}
                className="flex-1 h-10 bg-[var(--secondary)] border border-[var(--border)] text-[var(--secondary-foreground)] hover:bg-[var(--accent)] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                style={{ borderRadius: "var(--radius-button)" }}
              >
                <Dismiss20Regular />
                Cancelar
              </button>
              <button
                type="submit"
                disabled={!canSubmit}
                className="flex-1 h-10 bg-[var(--primary)] text-[var(--primary-foreground)] hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                style={{ borderRadius: "var(--radius-button)" }}
              >
                <Save20Regular />
                {isSaving ? "A guardar..." : "Guardar"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
